import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    useTheme,
    Avatar,
    Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BuildIcon from '@mui/icons-material/Build';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Herramienta, Obra } from '../../types';
import { format, isPast } from 'date-fns';
import { es } from 'date-fns/locale';

interface HerramientaListProps {
    herramientas: Herramienta[];
    obras: Obra[];
    onToolMove: (toolId: string, targetObraId: string | null) => Promise<void>;
    onEdit?: (herramienta: Herramienta) => void;
    onDelete?: (id: string) => void;
}

const HerramientaList: React.FC<HerramientaListProps> = ({ herramientas, obras, onToolMove, onEdit, onDelete }) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedTool, setSelectedTool] = useState<Herramienta | null>(null);

    // Group tools by obra
    const groupedTools = React.useMemo(() => {
        const groups: Record<string, Herramienta[]> = {};

        // Find ByB
        const obraByB = obras.find(o => o.nombre.toUpperCase() === 'BYB' || o.nombre.toUpperCase() === 'B&B');
        const bybId = obraByB?.id || 'byb_fallback';

        // Initialize ByB group first
        groups[bybId] = [];

        // Initialize other groups
        obras.forEach(obra => {
            if (obra.id !== bybId) {
                groups[obra.id] = [];
            }
        });

        // Add "Unassigned" group just in case
        groups['unassigned'] = [];

        herramientas.forEach(tool => {
            if (tool.obraId && groups[tool.obraId]) {
                groups[tool.obraId].push(tool);
            } else if (tool.obraId) {
                // If obra exists but not in our active list
                if (!groups[tool.obraId]) groups[tool.obraId] = [];
                groups[tool.obraId].push(tool);
            } else {
                // No obraId -> goes to ByB if exists, else unassigned
                if (groups[bybId]) {
                    groups[bybId].push(tool);
                } else {
                    groups['unassigned'].push(tool);
                }
            }
        });

        return groups;
    }, [herramientas, obras]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, tool: Herramienta) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedTool(tool);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedTool(null);
    };

    const handleMoveTool = (targetObraId: string | null) => {
        if (selectedTool) {
            onToolMove(selectedTool.id, targetObraId);
        }
        handleMenuClose();
    };

    const handleEditTool = () => {
        if (selectedTool && onEdit) {
            onEdit(selectedTool);
        }
        handleMenuClose();
    };

    const handleDeleteTool = () => {
        if (selectedTool && onDelete) {
            onDelete(selectedTool.id);
        }
        handleMenuClose();
    };

    const getObraName = (id: string) => {
        if (id === 'unassigned') return 'Sin Asignar';
        const obra = obras.find(o => o.id === id);
        return obra ? obra.nombre : 'Desconocida';
    };

    // Sort order: ByB first, then others alphabetically
    const sortedGroups = Object.keys(groupedTools).sort((a, b) => {
        const obraByB = obras.find(o => o.nombre.toUpperCase() === 'BYB' || o.nombre.toUpperCase() === 'B&B');
        const bybId = obraByB?.id;

        if (a === bybId) return -1;
        if (b === bybId) return 1;
        if (a === 'unassigned') return 1; // Put unassigned at bottom
        if (b === 'unassigned') return -1;

        const nameA = getObraName(a);
        const nameB = getObraName(b);
        return nameA.localeCompare(nameB);
    });

    return (
        <Box sx={{ pb: 4 }}>
            {sortedGroups.map(groupId => {
                const tools = groupedTools[groupId];
                if (tools.length === 0) return null; // Skip empty groups? Or show them? Let's skip for cleaner mobile view

                const obraByB = obras.find(o => o.nombre.toUpperCase() === 'BYB' || o.nombre.toUpperCase() === 'B&B');
                const isByB = groupId === obraByB?.id;
                const groupName = getObraName(groupId);

                return (
                    <Accordion
                        key={groupId}
                        defaultExpanded={isByB} // Expand ByB by default
                        disableGutters
                        elevation={0}
                        sx={{
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '8px !important',
                            '&:before': { display: 'none' }
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: isByB ? 'primary.main' : 'action.selected',
                                        color: isByB ? 'white' : 'action.active'
                                    }}
                                >
                                    {isByB ? <StoreIcon fontSize="small" /> : <BusinessIcon fontSize="small" />}
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {groupName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {tools.length} herramienta{tools.length !== 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            <List disablePadding>
                                {tools.map((tool, index) => {
                                    const isAlquilada = tool.tipo === 'alquilada';
                                    const isDue = isAlquilada && tool.fechaInicioAlquiler && tool.duracionAlquilerDias
                                        ? isPast(new Date(new Date(tool.fechaInicioAlquiler.toDate ? tool.fechaInicioAlquiler.toDate() : tool.fechaInicioAlquiler).setDate(new Date(tool.fechaInicioAlquiler.toDate ? tool.fechaInicioAlquiler.toDate() : tool.fechaInicioAlquiler).getDate() + tool.duracionAlquilerDias)))
                                        : false;

                                    return (
                                        <ListItem
                                            key={tool.id}
                                            divider={index !== tools.length - 1}
                                            secondaryAction={
                                                <IconButton
                                                    edge="end"
                                                    onClick={(e) => handleMenuOpen(e, tool)}
                                                    size="small"
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <BuildIcon color="action" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography variant="body2" fontWeight="500">
                                                            {tool.nombre} {tool.cantidad && tool.cantidad > 1 ? `(x${tool.cantidad})` : ''}
                                                        </Typography>
                                                        {isAlquilada && (
                                                            <Chip
                                                                label="Alq."
                                                                size="small"
                                                                color={isDue ? "error" : "default"}
                                                                variant="outlined"
                                                                sx={{ height: 16, fontSize: '0.6rem' }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={tool.descripcion || 'Sin descripción'}
                                                secondaryTypographyProps={{
                                                    variant: 'caption',
                                                    noWrap: true,
                                                    sx: { maxWidth: '200px', display: 'block' }
                                                }}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            {/* Move Tool Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: { maxHeight: 350, width: 250 }
                }}
            >
                {onEdit && (
                    <MenuItem onClick={handleEditTool}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Editar" />
                    </MenuItem>
                )}
                {onDelete && (
                    <MenuItem onClick={handleDeleteTool} sx={{ color: 'error.main' }}>
                        <ListItemIcon>
                            <DeleteOutlineIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText primary="Eliminar" />
                    </MenuItem>
                )}
                {(onEdit || onDelete) && <Divider />}

                <MenuItem disabled>
                    <Typography variant="caption" color="text.secondary">
                        Mover a...
                    </Typography>
                </MenuItem>

                {/* Option to move to Office/ByB */}
                <MenuItem onClick={() => handleMoveTool(null)}>
                    <ListItemIcon>
                        <StoreIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Oficina / Taller" />
                </MenuItem>

                {/* List all active obras */}
                {obras
                    .filter(o => o.estado === 'activa')
                    .sort((a, b) => a.nombre.localeCompare(b.nombre))
                    .map(obra => {
                        const obraByB = obras.find(o => o.nombre.toUpperCase() === 'BYB' || o.nombre.toUpperCase() === 'B&B');
                        if (obra.id === obraByB?.id) return null;

                        return (
                            <MenuItem key={obra.id} onClick={() => handleMoveTool(obra.id)}>
                                <ListItemIcon>
                                    <BusinessIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={obra.nombre} />
                            </MenuItem>
                        );
                    })
                }
            </Menu>
        </Box>
    );
};

export default HerramientaList;
